/**
 * Security utilities for input sanitization and validation
 * Prevents XSS attacks and data manipulation
 */

/**
 * Sanitize string input to prevent XSS attacks
 * Removes potentially dangerous characters and HTML tags
 */
export function sanitizeInput(input: string): string {
  if (typeof input !== 'string') {
    return '';
  }

  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');
  
  // Remove potentially dangerous characters
  sanitized = sanitized
    .replace(/[<>]/g, '') // Remove remaining angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers (onclick, onerror, etc.)
    .trim();

  return sanitized;
}

/**
 * Sanitize email input
 * Validates and sanitizes email addresses
 */
export function sanitizeEmail(email: string): string {
  if (typeof email !== 'string') {
    return '';
  }

  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const sanitized = email.trim().toLowerCase();

  // Return sanitized email if valid, otherwise empty string
  return emailRegex.test(sanitized) ? sanitizeInput(sanitized) : '';
}

/**
 * Sanitize phone number input
 * Removes non-numeric characters except +, -, spaces, and parentheses
 */
export function sanitizePhone(phone: string): string {
  if (typeof phone !== 'string') {
    return '';
  }

  // Allow only digits, +, -, spaces, and parentheses
  return phone.replace(/[^\d+\-() ]/g, '').trim();
}

/**
 * Sanitize text area content
 * More permissive than regular input but still removes dangerous content
 */
export function sanitizeTextArea(input: string): string {
  if (typeof input !== 'string') {
    return '';
  }

  // Remove script tags and event handlers
  let sanitized = input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/javascript:/gi, '');

  // Limit length to prevent DoS
  const maxLength = 10000;
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized.trim();
}

/**
 * Validate and sanitize form data object
 */
export function sanitizeFormData<T extends Record<string, any>>(data: T): T {
  const sanitized = { ...data };

  for (const key in sanitized) {
    if (typeof sanitized[key] === 'string') {
      if (key.toLowerCase().includes('email')) {
        sanitized[key] = sanitizeEmail(sanitized[key]) as T[Extract<keyof T, string>];
      } else if (key.toLowerCase().includes('phone')) {
        sanitized[key] = sanitizePhone(sanitized[key]) as T[Extract<keyof T, string>];
      } else if (key.toLowerCase().includes('message') || key.toLowerCase().includes('description')) {
        sanitized[key] = sanitizeTextArea(sanitized[key]) as T[Extract<keyof T, string>];
      } else {
        sanitized[key] = sanitizeInput(sanitized[key]) as T[Extract<keyof T, string>];
      }
    } else if (Array.isArray(sanitized[key])) {
      // Sanitize array elements
      sanitized[key] = sanitized[key].map((item: any) =>
        typeof item === 'string' ? sanitizeInput(item) : item
      ) as T[Extract<keyof T, string>];
    }
  }

  return sanitized;
}

/**
 * Validate CSRF token (placeholder for future API implementation)
 */
export function validateCSRFToken(token: string): boolean {
  // TODO: Implement CSRF token validation when adding API endpoints
  // For now, forms are client-side only, so this is a placeholder
  return typeof token === 'string' && token.length > 0;
}

/**
 * Rate limiting helper (client-side check)
 * Note: Real rate limiting should be implemented server-side
 */
const rateLimitStore = new Map<string, { count: number; resetTime: number }>();

export function checkRateLimit(identifier: string, maxRequests: number = 5, windowMs: number = 60000): boolean {
  const now = Date.now();
  const record = rateLimitStore.get(identifier);

  if (!record || now > record.resetTime) {
    rateLimitStore.set(identifier, { count: 1, resetTime: now + windowMs });
    return true;
  }

  if (record.count >= maxRequests) {
    return false;
  }

  record.count++;
  return true;
}

